import {
  adjustHue,
  darken,
  getLuminance,
  lighten,
  meetsContrastGuidelines,
  parseToHsl,
  parseToRgb,
  readableColor,
  rgba,
  toColorString
} from 'polished';

const getColorName = (hexColor: IHEX) => {
  const rgbToShortHex = (rgb: IRGB): string => {
    const hexR = Math.round(rgb.red / 17).toString(16);
    const hexG = Math.round(rgb.green / 17).toString(16);
    const hexB = Math.round(rgb.blue / 17).toString(16);
    return `#${hexR}${hexG}${hexB}`;
  };

  const isNamedColor = (namedHexColor: IHEX): string => {
    const uc = namedHexColor.toUpperCase();

    return Object.keys(namedColors).filter((key: string) => {
      const shorthand = rgbToShortHex(
        parseToRgb(namedColors[key])
      ).toUpperCase();
      return shorthand === uc ? key : '';
    })[0];
  };

  return isNamedColor(hexColor) || hexColor.toUpperCase();
};

const namedColors: INamedColorList = {
  aliceblue: '#F0F8FF',
  antiquewhite: '#FAEBD7',
  aqua: '#00FFFF',
  aquamarine: '#7FFFD4',
  azure: '#F0FFFF',
  beige: '#F5F5DC',
  bisque: '#FFE4C4',
  black: '#000000',
  blanchedalmond: '#FFEBCD',
  blue: '#0000FF',
  blueviolet: '#8A2BE2',
  brown: '#A52A2A',
  burlywood: '#DEB887',
  cadetblue: '#5F9EA0',
  chartreuse: '#7FFF00',
  chocolate: '#D2691E',
  coral: '#FF7F50',
  cornflowerblue: '#6495ED',
  cornsilk: '#FFF8DC',
  crimson: '#DC143C',
  cyan: '#00FFFF',
  darkblue: '#00008B',
  darkcyan: '#008B8B',
  darkgoldenrod: '#B8860B',
  darkgray: '#A9A9A9',
  darkgreen: '#006400',
  darkgrey: '#A9A9A9',
  darkkhaki: '#BDB76B',
  darkmagenta: '#8B008B',
  darkolivegreen: '#556B2F',
  darkorange: '#FF8C00',
  darkorchid: '#9932CC',
  darkred: '#8B0000',
  darksalmon: '#E9967A',
  darkseagreen: '#8FBC8F',
  darkslateblue: '#483D8B',
  darkslategray: '#2F4F4F',
  darkslategrey: '#2F4F4F',
  darkturquoise: '#00CED1',
  darkviolet: '#9400D3',
  deeppink: '#FF1493',
  deepskyblue: '#00BFFF',
  dimgray: '#696969',
  dimgrey: '#696969',
  dodgerblue: '#1E90FF',
  firebrick: '#B22222',
  floralwhite: '#FFFAF0',
  forestgreen: '#228B22',
  fuchsia: '#FF00FF',
  gainsboro: '#DCDCDC',
  ghostwhite: '#F8F8FF',
  gold: '#FFD700',
  goldenrod: '#DAA520',
  gray: '#808080',
  green: '#008000',
  greenyellow: '#ADFF2F',
  grey: '#808080',
  honeydew: '#F0FFF0',
  hotpink: '#FF69B4',
  indianred: '#CD5C5C',
  indigo: '#4B0082',
  ivory: '#FFFFF0',
  khaki: '#F0E68C',
  lavender: '#E6E6FA',
  lavenderblush: '#FFF0F5',
  lawngreen: '#7CFC00',
  lemonchiffon: '#FFFACD',
  lightblue: '#ADD8E6',
  lightcoral: '#F08080',
  lightcyan: '#E0FFFF',
  lightgoldenrodyellow: '#FAFAD2',
  lightgray: '#D3D3D3',
  lightgreen: '#90EE90',
  lightgrey: '#D3D3D3',
  lightpink: '#FFB6C1',
  lightsalmon: '#FFA07A',
  lightseagreen: '#20B2AA',
  lightskyblue: '#87CEFA',
  lightslategray: '#778899',
  lightslategrey: '#778899',
  lightsteelblue: '#B0C4DE',
  lightyellow: '#FFFFE0',
  lime: '#00FF00',
  limegreen: '#32CD32',
  linen: '#FAF0E6',
  magenta: '#FF00FF',
  maroon: '#800000',
  mediumaquamarine: '#66CDAA',
  mediumblue: '#0000CD',
  mediumorchid: '#BA55D3',
  mediumpurple: '#9370D8',
  mediumseagreen: '#3CB371',
  mediumslateblue: '#7B68EE',
  mediumspringgreen: '#00FA9A',
  mediumturquoise: '#48D1CC',
  mediumvioletred: '#C71585',
  midnightblue: '#191970',
  mintcream: '#F5FFFA',
  mistyrose: '#FFE4E1',
  moccasin: '#FFE4B5',
  navajowhite: '#FFDEAD',
  navy: '#000080',
  oldlace: '#FDF5E6',
  olive: '#808000',
  olivedrab: '#6B8E23',
  orange: '#FFA500',
  orangered: '#FF4500',
  orchid: '#DA70D6',
  palegoldenrod: '#EEE8AA',
  palegreen: '#98FB98',
  paleturquoise: '#AFEEEE',
  palevioletred: '#D87093',
  papayawhip: '#FFEFD5',
  peachpuff: '#FFDAB9',
  peru: '#CD853F',
  pink: '#FFC0CB',
  plum: '#DDA0DD',
  powderblue: '#B0E0E6',
  purple: '#800080',
  red: '#FF0000',
  rosybrown: '#BC8F8F',
  royalblue: '#4169E1',
  saddlebrown: '#8B4513',
  salmon: '#FA8072',
  sandybrown: '#F4A460',
  seagreen: '#2E8B57',
  seashell: '#FFF5EE',
  sienna: '#A0522D',
  silver: '#C0C0C0',
  skyblue: '#87CEEB',
  slateblue: '#6A5ACD',
  slategray: '#708090',
  slategrey: '#708090',
  snow: '#FFFAFA',
  springgreen: '#00FF7F',
  steelblue: '#4682B4',
  tan: '#D2B48C',
  teal: '#008080',
  thistle: '#D8BFD8',
  tomato: '#FF6347',
  turquoise: '#40E0D0',
  violet: '#EE82EE',
  white: '#FFFFFF'
};

interface INamedColorList {
  [key: string]: IHEX;
}

export interface IRGB {
  red: number;
  green: number;
  blue: number;
}

interface IHSL {
  hue: number;
  saturation: number;
  lightness: number;
}

type IDECIMAL = number;
type IHEX = string;

export interface IColor {
  name?: string;
  alpha: number;
  blue: number;
  decimal: IDECIMAL;
  green: number;
  hex: IHEX;
  hue: number;
  lightness: number;
  red: number;
  saturation: number;

  fromHSL(colorString: IHSL): void;
  fromRGB(colorString: IRGB): void;
  toString(): string;
  readableColor(): IHEX;
  named(name: string): Color;
}

export class Color implements IColor {
  name: string;
  alpha: number;
  blue: number;
  decimal: IDECIMAL;
  green: number;
  hex: IHEX;
  hue: number;
  lightness: number;
  red: number;
  saturation: number;

  // constructor(value: DECIMAL);
  constructor(hex: IHEX, alpha?: number) {
    if (hex) {
      const { hue, saturation, lightness } = parseToHsl(hex);
      const { red, green, blue } = parseToRgb(hex);

      this.red = red;
      this.green = green;
      this.blue = blue;
      this.alpha = alpha || 1;
      this.hex = hex;
      this.hue = hue;
      this.saturation = saturation;
      this.lightness = lightness;
      this.name = getColorName(hex);
      this.decimal = parseInt(hex, 16);
    }
  }

  fromHSL(value: IHSL): Color {
    const hex = toColorString(value);
    const { red, green, blue } = parseToRgb(hex);

    this.red = red;
    this.green = green;
    this.blue = blue;
    this.alpha = 1;
    this.hex = hex;
    this.hue = value.hue;
    this.saturation = value.saturation;
    this.lightness = value.lightness;
    this.name = getColorName(hex);
    this.decimal = parseInt(hex, 16);

    return this;
  }

  fromRGB(value: IRGB): Color {
    const hex = toColorString(value);
    const { hue, saturation, lightness } = parseToHsl(hex);

    this.red = value.red;
    this.green = value.green;
    this.blue = value.blue;
    this.alpha = 1;
    this.hex = hex;
    this.hue = hue;
    this.saturation = saturation;
    this.lightness = lightness;
    this.name = getColorName(hex);
    this.decimal = parseInt(hex, 16);

    return this;
  }

  withAlpha(value: number): Color {
    this.alpha = value;
    return this;
  }

  toString(): string {
    // tslint:disable: object-literal-sort-keys
    return rgba({
      red: this.red,
      green: this.green,
      blue: this.blue,
      alpha: this.alpha
    });
    // tslint:enable: object-literal-sort-keys
  }

  readableColor(): IHEX {
    return readableColor(this.hex);
  }

  named(name: string): Color {
    this.name = name;
    return this;
  }
}

// Provides a syntactic shortcut for .indexOf since .includes can't be used (not supported on IE)
export const keyMatchesString = (key: string, str: string) =>
  key.toLowerCase().indexOf(str) > -1;

export const isColor = (value: string) => {
  const firstTest: boolean = value.split(' ').length === 1;
  const secondTest: boolean =
    value.startsWith('#') ||
    value.startsWith('rgb') ||
    value.startsWith('rgba');

  return firstTest && secondTest;
};

export const isPrimaryColor = (key: string, { red, green, blue }: IRGB) => {
  const isPrimaryKey = keyMatchesString(key, 'primary');
  const isSecondaryKey = keyMatchesString(key, 'secondary');
  const isPrimaryRGB = 0.2126 * red + 0.7152 * green + 0.0722 * blue < 128;

  return isPrimaryKey || (!isSecondaryKey && isPrimaryRGB);
};

// Sets the second color's saturation and lightness to that of the first color
export const normalizeColor = (control: string, color: string): string => {
  const HSL_CONTROL = parseToHsl(control);
  const HSL_COLOR = parseToHsl(color);

  HSL_COLOR.saturation = HSL_CONTROL.saturation;
  HSL_COLOR.lightness = HSL_CONTROL.lightness;

  return toColorString(HSL_COLOR);
};

interface IAdjustColorArgs {
  a11y: boolean;
  control: string;
  experimental: string;
  variable: string;
}

// Creates a new, adjusted color based on the three provided colors
export const adjustColor = ({
  a11y,
  control,
  experimental,
  variable
}: IAdjustColorArgs) => {
  // Convert control and experimental variables to HSL
  const HSL_CONTROL = parseToHsl(control);
  const HSL_EXPERIMENTAL = parseToHsl(experimental);

  // Calculate H,S, and L difference between control and experimental
  const hueDiff = HSL_CONTROL.hue - HSL_EXPERIMENTAL.hue;
  const satDiff = HSL_CONTROL.saturation - HSL_EXPERIMENTAL.saturation;
  const lightDiff = HSL_CONTROL.lightness - HSL_EXPERIMENTAL.lightness;

  // Adjust hue of variable (new) color and convert to HSL
  const huelFinal = adjustHue(-hueDiff, variable);
  const hslFinal = parseToHsl(huelFinal);

  // Set lightness to HSL_EXPERIMENTAL.lightness
  hslFinal.lightness = HSL_CONTROL.lightness - lightDiff;
  // Set saturation to HSL_EXPERIMENTAL.saturation
  hslFinal.saturation = HSL_CONTROL.saturation - satDiff;
  // TODO: Type error.
  // Set alpha to HSL_EXPERIMENTAL.alpha
  // hslFinal.alpha = HSL_EXPERIMENTAL.alpha;

  // Convert back to hex or rgba color string
  let finalColor = toColorString(hslFinal);

  // Adjust to meet accessibility requirements
  if (a11y) {
    let lumi = getLuminance(finalColor);
    while (
      !meetsContrastGuidelines(finalColor, lumi > 0.5 ? '#000' : '#fff').AA
    ) {
      finalColor =
        lumi > 0.5 ? lighten(0.01, finalColor) : darken(0.05, finalColor);
      lumi = getLuminance(finalColor);
    }
  }
  return finalColor;
};
